import type { ProductPrice as ProductPriceSchema } from '../../../../../amplienceTypes/schemas/imported/product-price-schema';
import type { Price, StyleVariant } from '../../../../../utilities/graphql/codegen';

import { useCallback, useEffect, useRef, useState } from 'react';

import { PriceTax } from './PriceTax';
import { ProductPriceConstructor } from './ProductPriceConstructor';
import { ProductPriceRrpTooltip } from './ProductPriceRrpTooltip';
import { ProductPriceRrpTooltipToggler } from './ProductPriceRrpTooltipToggler';
import { ProductPriceSummaryForex } from './ProductPriceSummaryForex';
import { S } from './styles';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { LowestPriceDisplay, PriceDisplayMode } from '~/utilities/graphql/codegen';
import { parseFormattedPrice } from '~/utilities/helpers/parseFormattedPrice';
import { useMembersOnlyPrices } from '~/utilities/hooks/useMembersOnlyPrices';
import { usePriceDisplayConfiguration } from '~/utilities/hooks/usePriceDisplayConfiguration';

export interface ProductPriceHybrisProps {
  productStyleVariantInfo?: StyleVariant | null;
  price?: Price | null;
  personalisedPrice?: Price | null;
  fromPrice?: Price | null;
  intermediaryPrice?: Price | null;
  formattedLowestPrice?: string | null;
  isQuickviewOverlay?: boolean;
}

export const ProductPrice = ({
  basePriceColor,
  taxV2 = '',
  lowestPrice30days = '',
  originalPrice = '',
  productStyleVariantInfo,
  price,
  personalisedPrice,
  rrpTooltip,
  fromPrice,
  intermediaryPrice,
  formattedLowestPrice,
  isQuickviewOverlay,
}: ProductPriceHybrisProps & ProductPriceSchema) => {
  const [tooltipOpened, setTooltipOpened] = useState(false);

  const { product } = usePDPContext();
  const { isMobileOrTablet } = useMediaQueryContext();
  const {
    configuration: { lowestPriceDisplay, showLowestPriceText },
  } = useStaticContext();

  const ref = useRef<HTMLDivElement>(null);
  const originalPriceRef = useRef<HTMLDivElement>(null);

  const toggleTooltip = () => setTooltipOpened(!tooltipOpened);
  const closeTooltip = () => setTooltipOpened(false);

  const { priceFormatted, priceValue, applyPersonalisedPrice } = useMembersOnlyPrices({
    price,
    personalisedPrice,
    productStyleVariantInfo,
  });

  const outsideClicked = useCallback(
    (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node) && tooltipOpened) {
        closeTooltip();
      }
    },
    [tooltipOpened]
  );

  useEffect(() => {
    document.addEventListener('click', outsideClicked);
  }, [outsideClicked, tooltipOpened]);

  /**
   * Tooltip position
   * In case of long text the tooltip will show position on the right side
   *
   * return the X value for position in percent
   */
  const getTooltipPosition = useCallback(() => {
    if (isMobileOrTablet) {
      return 3;
    }

    const originalPriceLeftOffset = originalPriceRef.current
      ? originalPriceRef.current.offsetLeft
      : 0;

    if (originalPriceLeftOffset === 0) {
      const originalPriceWidth = originalPriceRef.current
        ? originalPriceRef.current.offsetWidth
        : 0;

      return originalPriceWidth > 280 ? 47 : 50;
    }

    if (originalPriceLeftOffset < 280) {
      return originalPriceLeftOffset < 150 ? 47 : 70;
    }

    return 50;
  }, [originalPriceRef, isMobileOrTablet]);

  const priceDisplayConfig = usePriceDisplayConfiguration(product.priceDisplayMode);

  // Branching into the new price config
  if (LowestPriceDisplay.Config === lowestPriceDisplay && priceDisplayConfig) {
    return (
      <ProductPriceConstructor
        basePriceColor={basePriceColor}
        taxV2={taxV2}
        price={price}
        personalisedPrice={personalisedPrice}
        fromPrice={fromPrice}
        intermediaryPrice={intermediaryPrice}
        formattedLowestPrice={formattedLowestPrice}
        priceDisplayConfig={priceDisplayConfig}
        showInOneRow={priceDisplayConfig.displayMode === PriceDisplayMode.DefaultWithRrp}
      />
    );
  }

  const isOnlyLowestPrice =
    lowestPriceDisplay === LowestPriceDisplay.OnlyLowestPrice && showLowestPriceText;

  const variantFromPriceFormatted = applyPersonalisedPrice
    ? productStyleVariantInfo?.formattedBasePrice
    : productStyleVariantInfo?.formattedFromPrice;

  const variantFromPriceValue = applyPersonalisedPrice
    ? parseFloat(productStyleVariantInfo?.unformattedBasePrice || '0')
    : parseFloat(productStyleVariantInfo?.unformattedFromPrice || '0');

  const fromPriceFormatted = productStyleVariantInfo?.formattedBasePrice
    ? variantFromPriceFormatted
    : fromPrice?.formattedValue;

  const fromPriceValue = productStyleVariantInfo?.formattedBasePrice
    ? variantFromPriceValue
    : fromPrice?.value;

  const intermediaryPriceFormatted = productStyleVariantInfo?.formattedBasePrice
    ? productStyleVariantInfo?.formattedIntermediaryPrice
    : intermediaryPrice?.formattedValue;
  const intermediaryPriceValue = productStyleVariantInfo?.formattedBasePrice
    ? parseFloat(productStyleVariantInfo?.unformattedIntermediaryPrice || '0')
    : intermediaryPrice?.value;

  const originalPricePercentage =
    fromPriceValue && priceValue ? Math.round((priceValue / fromPriceValue) * 100 - 100) : null;

  const originalPriceCopy =
    fromPriceFormatted && (originalPricePercentage || originalPricePercentage === 0)
      ? `${originalPrice} ${fromPriceFormatted}`
      : '';

  const lowestPriceFormatted = productStyleVariantInfo?.formattedLowestPrice
    ? productStyleVariantInfo?.formattedLowestPrice
    : formattedLowestPrice;

  const lowestPriceValue = lowestPriceFormatted ? parseFormattedPrice(lowestPriceFormatted) : null;

  const isRRPLast30Days = fromPriceValue === lowestPriceValue;

  const lowestPriceCopy = lowestPriceFormatted ? (
    <>
      {lowestPrice30days}&nbsp;
      <span>{lowestPriceFormatted}</span>
    </>
  ) : (
    ''
  );

  if (priceFormatted) {
    const hasDiscountPrice: boolean =
      !!fromPriceFormatted && (fromPriceValue || 0) > (priceValue || 0);

    const hasDoubleStrikeThroughPrice: boolean =
      !!fromPriceFormatted &&
      !!intermediaryPriceFormatted &&
      (fromPriceValue || 0) > (intermediaryPriceValue || 0) &&
      (priceValue || 0) < (intermediaryPriceValue || 0);

    return (
      <S.ProductPriceWrapper
        $lowestPriceDisplay={lowestPriceDisplay || 'NEVER'}
        data-testid="price"
        className="productPriceWrapper"
      >
        {lowestPriceDisplay === 'NEVER' && (
          <>
            {hasDiscountPrice ? (
              <>
                <S.PriceDiscount data-testid="summary-before-price" data-cs-capture="">
                  {fromPriceFormatted}
                </S.PriceDiscount>
                {hasDoubleStrikeThroughPrice && (
                  <S.PriceDiscount data-testid="summary-intermediary-price" data-cs-capture="">
                    {intermediaryPriceFormatted}
                  </S.PriceDiscount>
                )}
                <S.PriceValue
                  data-testid="summary-product-price"
                  $basePriceColor={basePriceColor}
                  $hasDiscountPrice={Boolean(hasDiscountPrice)}
                  data-cs-capture=""
                >
                  {priceFormatted}
                </S.PriceValue>
                <PriceTax taxV2={taxV2} />
              </>
            ) : (
              <>
                <S.PriceValue
                  data-testid="summary-product-price"
                  $basePriceColor={basePriceColor}
                  $hasDiscountPrice={Boolean(hasDiscountPrice)}
                  data-cs-capture=""
                >
                  {priceFormatted}
                </S.PriceValue>
                <PriceTax taxV2={taxV2} />
              </>
            )}
          </>
        )}
        {lowestPriceDisplay !== 'NEVER' && (
          <>
            <S.PriceInfo
              ref={ref}
              $hasRrpTooltip={!!rrpTooltip}
              data-testid={
                lowestPriceCopy ? 'summary-discount-lowest-price' : 'summary-before-price'
              }
            >
              {hasDiscountPrice && (
                <>
                  {rrpTooltip &&
                    !isQuickviewOverlay &&
                    lowestPriceDisplay !== 'ONLY_LOWEST_PRICE' && (
                      <>
                        <S.TooltipContainer>
                          <ProductPriceRrpTooltipToggler
                            onClick={toggleTooltip}
                            tooltipOpened={tooltipOpened}
                          />
                          {tooltipOpened && (
                            <>
                              <S.IconArrow />
                              <ProductPriceRrpTooltip
                                onClick={closeTooltip}
                                content={rrpTooltip}
                                position={getTooltipPosition()}
                              />
                            </>
                          )}
                        </S.TooltipContainer>
                      </>
                    )}
                  {originalPriceCopy && (
                    <S.PriceOriginal
                      data-testid="summary-before-price"
                      data-cs-capture=""
                      ref={originalPriceRef}
                      $isOnlyLowestPrice={isOnlyLowestPrice}
                    >
                      {isOnlyLowestPrice ? '' : originalPriceCopy}
                    </S.PriceOriginal>
                  )}
                </>
              )}
              <S.PriceValue
                data-testid="summary-product-price"
                $basePriceColor={basePriceColor}
                $hasDiscountPrice={Boolean(hasDiscountPrice)}
                data-cs-capture=""
              >
                {priceFormatted}
                {priceValue && <ProductPriceSummaryForex price={priceValue} />}
              </S.PriceValue>
              <PriceTax taxV2={taxV2} />
            </S.PriceInfo>
            {((hasDiscountPrice &&
              !(lowestPriceDisplay === LowestPriceDisplay.DiscountsOnly && isRRPLast30Days)) ||
              (!hasDiscountPrice &&
                lowestPriceCopy &&
                lowestPriceDisplay === LowestPriceDisplay.Always)) && (
              <S.PriceLowest30Days data-testid="lowest-price-text" data-cs-capture="">
                {lowestPriceCopy}
              </S.PriceLowest30Days>
            )}
          </>
        )}
      </S.ProductPriceWrapper>
    );
  }

  return <></>;
};
